'use client';

import { useTranslation } from 'react-i18next';
import type { NotificationItem } from '@/lib/backend-api';
import { cn } from '@/lib/utils';
import { LocaleLink } from '@/components/locale-link';
import { describeNotification } from './describe';

/**
 * Where a notification sends the user.
 *
 * Deliberately category-based rather than company-based: the store keeps
 * `eu_id`, but company pages live at slug URLs built from name + Sitz +
 * register id (`generateCompanySlug`) and the notification payload carries
 * none of that — while the eu_id route (`/companies/[euId]`) is deprecated
 * and 404s. Linking to the watchlist, which owns the slug map and shows the
 * item in context, beats fabricating a URL that would break.
 *
 * `?company` + `?n` carry the click through: the watchlist expands that
 * company and outlines the entry, so a click lands *on* the change rather
 * than at the top of a list the user then has to search.
 */
export function hrefFor(n: NotificationItem): string | null {
  switch (n.category) {
    case 'watchlist':
    case 'lists': {
      if (!n.eu_id) return '/watchlist';
      const params = new URLSearchParams({ company: n.eu_id, n: String(n.id) });
      return `/watchlist?${params.toString()}`;
    }
    case 'downloads': {
      // Land on the document itself, not the top of the list: the hub can
      // hold hundreds of rows and the notification is about exactly one.
      const key = (n.payload as { resource_key?: string } | null)?.resource_key;
      return key
        ? `/activity?tab=downloads&key=${encodeURIComponent(key)}`
        : '/activity?tab=downloads';
    }
    case 'feeds': {
      // A feed event is about a company we may not hold a slug for, so it
      // lands on the page the feed mirrors. An internal path in the payload
      // (if the producer adds one) wins.
      const path = (n.payload as { path?: unknown } | null)?.path;
      if (typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) {
        return path;
      }
      switch (n.event_type) {
        case 'feed_insolvency':
          return '/insolvenzticker';
        case 'feed_new_company':
          return '/neugruendungen';
        case 'feed_tender':
          return '/vergabe';
        case 'feed_saved_search':
          return '/my-lists?tab=searches';
        default:
          return '/account/notifications#my-alerts';
      }
    }
    case 'credits':
      if (n.event_type === 'alerts_paused') return '/account/notifications#my-alerts';
      return n.event_type === 'credits_granted' ? '/account/usage' : '/plans';
    case 'billing':
      return '/account/billing';
    default:
      return null;
  }
}

function formatWhen(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale.startsWith('de') ? 'de-DE' : 'en-US', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * One notification in the bell dropdown or the history list. Clicking marks
 * it read; unread rows carry a tinted background and a dot.
 */
export function NotificationRow({
  notification: n,
  onMarkRead,
  disabled,
  onNavigate,
}: {
  notification: NotificationItem;
  onMarkRead: (id: number) => void;
  disabled: boolean;
  onNavigate?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { icon, label } = describeNotification(n, t);
  const unread = n.read_at == null;
  const href = hrefFor(n);
  const critical = n.severity === 'critical';
  // Feed events about a company we hold no row for carry the name in the
  // payload instead of the joined `company_name`.
  const payloadName = (n.payload as { company_name?: unknown } | null)
    ?.company_name;
  const companyName =
    n.company_name ??
    (typeof payloadName === 'string' && payloadName ? payloadName : null);

  const body = (
    <>
      <span
        className={cn(
          'mt-0.5 shrink-0',
          critical ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            'text-sm leading-snug',
            unread ? 'font-medium' : 'font-normal',
          )}
        >
          {label}
        </span>
        {companyName && (
          <span className="truncate text-xs text-muted-foreground">
            {companyName}
          </span>
        )}
        <span className="text-[11px] text-muted-foreground">
          {formatWhen(n.created_at, i18n.language ?? 'en')}
        </span>
      </span>
      {unread && (
        <span
          aria-label={t('notifications.unread', 'Unread')}
          className="mt-1.5 size-2 shrink-0 rounded-full"
          style={{ background: 'var(--fd-accent-ink, #c2410c)' }}
        />
      )}
    </>
  );

  const className = cn(
    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
    unread && 'bg-muted/30',
  );

  const handleClick = () => {
    if (unread && !disabled) onMarkRead(n.id);
    onNavigate?.();
  };

  return (
    <li>
      {href ? (
        <LocaleLink href={href} onClick={handleClick} className={className}>
          {body}
        </LocaleLink>
      ) : (
        <button type="button" onClick={handleClick} className={className}>
          {body}
        </button>
      )}
    </li>
  );
}
